import { ProfileAntispywareAndDns, Rule, SecurityType } from '../dtos/version.dto';
import { EditableRule, MultiSelectType } from '../model/EditableRule';
import { ProfilesOptions, Zone } from './enums';
import { useStores } from '../components/store';
import { RuleHelper, RuleNameAlias } from './rule-helper';
import { lowerCase } from 'lodash';

export const MAX_CUSTOM_RULE_COUNT = 25;

export const isAllowedRule = (ruleAction?: 'allow' | 'drop') => {
    return ruleAction === 'allow';
};

export const isAppRule = (ruleName: string) => {
    return [RuleHelper.getRuleName(RuleNameAlias.TRUST_TO_UNTRUST_APPS),
    RuleHelper.getRuleName(RuleNameAlias.UNTRUST_TO_TRUST_APPS)].includes(ruleName);
};

export const isAllowListRule = (ruleName: string) => {
    return [RuleHelper.getRuleName(RuleNameAlias.ALLOW_UNTRUST_DEST),
    RuleHelper.getRuleName(RuleNameAlias.ALLOW_UNTRUST_SRC)].includes(ruleName);
};

export const isAllowedServiceRule = (ruleName: string) => {
    return [RuleHelper.getRuleName(RuleNameAlias.TRUST_TO_UNTRUST_SERVICES),
    RuleHelper.getRuleName(RuleNameAlias.UNTRUST_TO_TRUST_SERVICES)].includes(ruleName);
};

export const isAllowedNonWebServiceRule = (ruleName: string) => {
    return [RuleHelper.getRuleName(RuleNameAlias.ALLOW_TRUST_TO_UNTRUST_NON_WEB_SERVICES),
    RuleHelper.getRuleName(RuleNameAlias.ALLOW_UNTRUST_TO_TRUST_NON_WEB_SERVICES)].includes(ruleName);
};

export const isAllowedWebServiceRule = (ruleName: string) => {
    return [RuleHelper.getRuleName(RuleNameAlias.TRUST_TO_UNTRUST_SERVICES),
    RuleHelper.getRuleName(RuleNameAlias.UNTRUST_TO_TRUST_SERVICES)].includes(ruleName);
};

export const isBlockServiceRule = (ruleName: string) => {
    return [RuleHelper.getRuleName(RuleNameAlias.TRUST_TO_UNTRUST_BLOCKED_SERVICES),
    RuleHelper.getRuleName(RuleNameAlias.UNTRUST_TO_TRUST_BLOCKED_SERVICES)].includes(ruleName);
};
export const isServiceRule = (ruleName: string) => isAllowedServiceRule(ruleName) || isAllowedNonWebServiceRule(ruleName) || isBlockServiceRule(ruleName);
export const isWebServiceRule = (ruleName: string) => isAllowedWebServiceRule(ruleName);

export function getUpdatedBy(rule: Rule) {
    if ('system' === rule.lastUpdaterMetadata.firstName?.toLowerCase()) {
        return rule.lastUpdaterMetadata.firstName;
    }
    if (rule.lastUpdaterMetadata.firstName && rule.lastUpdaterMetadata.lastName) {
        return `${rule.lastUpdaterMetadata.firstName} ${rule.lastUpdaterMetadata.lastName}`;
    }
    return rule.lastUpdaterMetadata.email || '';
}

export const useRuleConverter = () => {

    const { fwVersionStore } = useStores();

    const zoneToString = (zone?: string) => {
        const sid = fwVersionStore.selectedFwVersion?.sid;
        if (zone === Zone.MY_NETWORK) {
            return `${sid}_trust`;
        }

        if (zone === Zone.INTERNET) {
            return `${sid}_untrust`;
        }

        return zone;
    };

    const stringToZone = (input: string): Zone | undefined => {
        if (!input) return undefined;
        const [, zoneStr] = input.split('_');
        return zoneStr === 'trust' ? Zone.MY_NETWORK : Zone.INTERNET;
    };

    const profileToString = (value?: string) => {
        return value === ProfilesOptions.INACTIVE ? undefined : lowerCase(value);
    };

    const getUrlFilteringValue = (value: string) => {
        if (value === ProfilesOptions.INACTIVE) {
            return '';
        }

        const urlFiltering = fwVersionStore.selectedFwVersion?.content?.policy.url_filtering?.[0];
        return urlFiltering?.id ?? '';
    };

    const stringToProfile = (value?: string) => {
        if (!value) {
            return ProfilesOptions.INACTIVE;
        }

        const valueParts = value.split('_');
        return (ProfilesOptions as any)[valueParts.at(-1)!.toUpperCase()];
    };

    const stringToSpywareOrDnsProfile = (profiles: ProfileAntispywareAndDns[], value: string | undefined, field: 'antispyware' | 'dns') => {
        if (!value) {
            return ProfilesOptions.INACTIVE;
        }
        const matchProfile = profiles?.find(profile => profile.id === value);
        if (!matchProfile) {
            return ProfilesOptions.INACTIVE;
        }
        return (ProfilesOptions as any)[matchProfile[field].toUpperCase()];
    };


    const convertEditableToRule = (editableRule: EditableRule): Partial<Rule> => {
        return {
            id: editableRule.id,
            displayName: editableRule.name,
            action: editableRule.action,
            disabled: !editableRule.active,
            description: editableRule.description,
            isDefault: editableRule.isDefault,
            position: editableRule.position,

            application: { applications: editableRule.applications },
            service: { services: editableRule.services, service_groups: [] },


            profiles: {
                url_filtering: getUrlFilteringValue(editableRule.urlFiltering),
                anti_spyware: profileToString(editableRule.antiSpyware),
                dns: profileToString(editableRule.dns),
                anti_virus: profileToString(editableRule.antiVirus),
                vulnerability_protection: profileToString(editableRule.vulnerabilityProtection)
            },

            src: {
                zone: [zoneToString(editableRule.srcZone) || ''],
                address: {
                    address_groups: editableRule.srcAddressGroups,
                    regions: editableRule.srcRegions,
                    addresses: [],
                    edls: editableRule.srcEdls
                }
            },


            dst: {
                zone: [zoneToString(editableRule.dstZone) || ''],
                address: {
                    address_groups: editableRule.dstAddressGroups,
                    regions: editableRule.dstRegions,
                    addresses: [],
                    edls: editableRule.dstEdls
                }
            },
        };
    };

    const generateMultiselectType = (data?: any[]) => {
        return data?.length ? MultiSelectType.SELECTED : MultiSelectType.ANY;
    };

    const convertRuleToEditable = (rule: Rule): EditableRule => {
        const profiles = fwVersionStore.selectedFwVersion?.content.policy.profiles || [];
        return {
            id: rule.id,
            name: rule.displayName,
            action: rule.action,
            active: !rule.disabled,
            description: rule.description,
            isDefault: rule.isDefault,
            position: fwVersionStore.calculateRulePosition(rule.id),

            applications: rule.application.applications,
            applicationsType: generateMultiselectType(rule.application.applications),
            services: rule.service.services,
            servicesType: isServiceRule(rule.displayName) ? MultiSelectType.SELECTED : generateMultiselectType(rule.service.services),
            urlFiltering: !rule.profiles.url_filtering ? ProfilesOptions.INACTIVE : 'Active',
            antiSpyware: stringToSpywareOrDnsProfile(profiles, rule.profiles[SecurityType.AntiSpyware], 'antispyware'),
            antiVirus: stringToProfile(rule.profiles[SecurityType.AntiVirus]),
            dns: stringToSpywareOrDnsProfile(profiles, rule.profiles[SecurityType.AntiSpyware], 'dns'),
            vulnerabilityProtection: stringToProfile(rule.profiles[SecurityType.VulnerabilityProtection]),
            srcZone: stringToZone(rule.src.zone?.[0]),
            srcRegions: rule.src.address.regions,
            srcRegionsType: generateMultiselectType(rule.src.address.regions),
            srcAddressGroups: rule.src.address.address_groups,
            srcAddressGroupsType: generateMultiselectType(rule.src.address.address_groups),
            srcEdls: rule.src.address.edls,

            dstZone: stringToZone(rule.dst.zone?.[0]),
            dstRegions: rule.dst.address.regions,
            dstRegionsType: generateMultiselectType(rule.dst.address.regions),
            dstAddressGroups: rule.dst.address.address_groups,
            dstAddressGroupsType: generateMultiselectType(rule.dst.address.address_groups),
            dstEdls: rule.dst.address.edls,
            profiles,
        };
    };

    const generateNewRuleName = (): string => {
        const baseName = 'New Custom Rule';
        const nameRegex = /^New Custom Rule(?: (\d+))?$/;

        const existingNumbers = new Set(
            fwVersionStore.selectedFwVersion?.content.policy.rules
                .map((rule) => nameRegex.exec(rule.displayName))
                .filter(Boolean)
                .map((match) => Number.parseInt(match?.[1] || '0', 10))
        );

        let counter = 0;
        while (existingNumbers.has(counter)) {
            counter++;
        }

        return counter === 0 ? baseName : `${baseName} ${counter}`;
    };

    const prepareRuleForSaving = (editableRule: EditableRule, originalRule?: Rule): Partial<Rule> => {
        const rule = convertEditableToRule(editableRule);

        if (rule.src?.address.addresses && originalRule?.src.address.addresses) {
            rule.src.address.addresses = originalRule?.src.address.addresses;
        }
        if (rule.dst?.address.addresses && originalRule?.dst.address.addresses) {
            rule.dst.address.addresses = originalRule?.dst.address.addresses;
        }
        if (isServiceRule(editableRule.name) && !editableRule.services.length) {
            rule.disabled = true;
        }

        return rule;
    };


    return { convertEditableToRule, convertRuleToEditable, generateNewRuleName, prepareRuleForSaving };
};